/**
 * TemporalSlider
 *
 * Bottom-of-map playback control.
 * - Dual-handle range slider (start / end year)
 * - Play/Pause button that animates the end date forward
 */
import { useCallback, useEffect, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';

const MIN_YEAR = 2020;
const MAX_YEAR = new Date().getFullYear();
const PLAY_INTERVAL_MS = 800; // how fast the playback advances

function yearToDate(year, end = false) {
  return end ? `${year}-12-31` : `${year}-01-01`;
}

function dateToYear(dateStr) {
  return parseInt(dateStr.slice(0, 4), 10);
}

export default function TemporalSlider() {
  const { dateRange, setDateRange, isPlaying, setIsPlaying } = useMapStore();
  const intervalRef = useRef(null);

  const startYear = dateToYear(dateRange.start);
  const endYear = dateToYear(dateRange.end);

  // ── Playback logic ────────────────────────────────────────────────────────
  const startPlayback = useCallback(() => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      useMapStore.setState((s) => {
        const currentEnd = dateToYear(s.dateRange.end);
        if (currentEnd >= MAX_YEAR) {
          clearInterval(intervalRef.current);
          return { isPlaying: false };
        }
        return {
          dateRange: {
            start: s.dateRange.start,
            end: yearToDate(currentEnd + 1, true),
          },
        };
      });
    }, PLAY_INTERVAL_MS);
  }, [setIsPlaying]);

  const stopPlayback = useCallback(() => {
    clearInterval(intervalRef.current);
    setIsPlaying(false);
  }, [setIsPlaying]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleTogglePlay = () => {
    if (isPlaying) stopPlayback();
    else startPlayback();
  };

  // ── Slider handlers ───────────────────────────────────────────────────────
  const handleStartChange = (e) => {
    const year = Math.min(Number(e.target.value), endYear - 1);
    setDateRange({ start: yearToDate(year), end: dateRange.end });
  };

  const handleEndChange = (e) => {
    const year = Math.max(Number(e.target.value), startYear + 1);
    setDateRange({ start: dateRange.start, end: yearToDate(year, true) });
  };

  // Calculate fill percentages
  const totalRange = MAX_YEAR - MIN_YEAR;
  const leftPct = ((startYear - MIN_YEAR) / totalRange) * 100;
  const rightPct = ((endYear - MIN_YEAR) / totalRange) * 100;

  return (
    <div className="mx-4 mb-4 rounded-2xl border border-neutral-200 bg-paper/90 px-6 py-4 shadow-2xl backdrop-blur-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-widest text-neutral-500">
          Timeline
        </span>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span className="rounded bg-parchment border border-neutral-200 px-2 py-0.5 font-mono text-ink">{startYear}</span>
          <span>→</span>
          <span className="rounded bg-parchment border border-neutral-200 px-2 py-0.5 font-mono text-ink">{endYear}</span>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Play / Pause button */}
        <button
          onClick={handleTogglePlay}
          aria-label={isPlaying ? 'Pause playback' : 'Play timeline'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#003cc5] text-parchment shadow transition hover:bg-[#0036b1] active:scale-95"
        >
          {isPlaying ? (
            // Pause icon
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            // Play icon
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        {/* Dual range slider */}
        <div className="relative flex-1">
          {/* Track background */}
          <div className="h-1.5 w-full rounded-full bg-neutral-200">
            {/* Active fill between handles */}
            <div
              className="absolute h-1.5 rounded-full bg-[#003cc5]"
              style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
            />
          </div>

          {/* Start handle */}
          <input
            type="range"
            min={MIN_YEAR}
            max={MAX_YEAR}
            value={startYear}
            onChange={handleStartChange}
            className="temporal-slider absolute inset-0 h-full w-full cursor-pointer opacity-0"
            style={{ zIndex: startYear > endYear - 2 ? 2 : 1 }}
          />

          {/* End handle */}
          <input
            type="range"
            min={MIN_YEAR}
            max={MAX_YEAR}
            value={endYear}
            onChange={handleEndChange}
            className="temporal-slider absolute inset-0 h-full w-full cursor-pointer opacity-0"
            style={{ zIndex: 2 }}
          />

          {/* Visible thumb markers */}
          <div
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#003cc5] bg-white shadow"
            style={{ left: `${leftPct}%` }}
          />
          <div
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#003cc5] bg-white shadow"
            style={{ left: `${rightPct}%` }}
          />
        </div>
      </div>

      {/* Year ticks */}
      <div className="mt-2 flex justify-between px-1 text-[10px] text-neutral-400">
        {Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => MIN_YEAR + i).map((y) => (
          <span key={y}>{y}</span>
        ))}
      </div>
    </div>
  );
}
